import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Modal from '../components/Modal';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const fetchDepartmentOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get(`/masters/departments?search=${encodeURIComponent(search)}`);
  return res.data.departments.map((d: any) => ({ value: d.id, label: `${d.name} (${d.code})` }));
};

export default function OrgChart() {
  const [charts, setCharts] = useState<any[]>([]);
  const [chartName, setChartName] = useState('');
  const [activeChart, setActiveChart] = useState<any>(null);
  const [tree, setTree] = useState<any>(null);
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [posForm, setPosForm] = useState({ positionLevel: 'Top Management', employeeId: '', departmentId: '', positionOrder: '0' });

  const fetchCharts = async () => {
    const res = await api.get('/hr/org-chart');
    setCharts(res.data.charts || []);
  };

  useEffect(() => {
    fetchCharts();
  }, []);

  const createChart = async () => {
    if (!chartName) return;
    await api.post('/hr/org-chart', { chartName });
    setChartName('');
    fetchCharts();
  };

  const openPreview = async (chart: any) => {
    setActiveChart(chart);
    const res = await api.get(`/hr/org-chart/${chart.id}/preview`);
    setTree(res.data.tree);
  };

  const addPosition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeChart) return;
    try {
      await api.post(`/hr/org-chart/${activeChart.id}/positions`, {
        ...posForm,
        positionOrder: Number(posForm.positionOrder),
      });
      setShowAddPosition(false);
      setPosForm({ positionLevel: 'Top Management', employeeId: '', departmentId: '', positionOrder: '0' });
      openPreview(activeChart);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to add position');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Organization Chart</h1>

      <div className="bg-white rounded-lg shadow p-6 flex gap-3">
        <Input placeholder="New chart name" value={chartName} onChange={(e) => setChartName(e.target.value)} className="flex-1" />
        <Button onClick={createChart}>Create Chart</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {charts.map((c) => (
          <div key={c.id} className="bg-white rounded-lg shadow p-4 flex justify-between items-center">
            <span className="font-medium">{c.chart_name}</span>
            <button className="text-blue-600 hover:underline text-sm" onClick={() => openPreview(c)}>
              Preview / Edit
            </button>
          </div>
        ))}
      </div>

      {activeChart && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">{activeChart.chart_name}</h2>
            <Button onClick={() => setShowAddPosition(true)}>Add Position</Button>
          </div>

          {tree && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Top Management</h3>
                <div className="flex gap-3 flex-wrap">
                  {tree.topManagement.map((p: any) => (
                    <div key={p.id} className="border rounded-lg px-4 py-2 bg-blue-50">{p.employee_name || '(unassigned)'}</div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Middle Management</h3>
                <div className="flex gap-3 flex-wrap">
                  {tree.middleManagement.map((p: any) => (
                    <div key={p.id} className="border rounded-lg px-4 py-2 bg-gray-50">{p.employee_name || '(unassigned)'}</div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Departments</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {tree.departments.map((d: any) => (
                    <div key={d.id} className="border rounded-lg p-4">
                      <p className="font-semibold">{d.department_name}</p>
                      <p className="text-sm text-gray-600 mb-2">Head: {d.employee_name}</p>
                      {d.sections.map((s: any) => (
                        <p key={s.id} className="text-xs text-gray-500 pl-2">
                          ↳ {s.section_name}: {s.employee_name}
                        </p>
                      ))}
                    </div>
                  ))}
                  {tree.departments.length === 0 && <p className="text-sm text-gray-500">No department heads assigned yet</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal isOpen={showAddPosition} onClose={() => setShowAddPosition(false)} title="Add Position">
        <form onSubmit={addPosition} className="space-y-4">
          <Select
            label="Position Level"
            value={posForm.positionLevel}
            onChange={(e) => setPosForm({ ...posForm, positionLevel: e.target.value })}
            options={[
              { value: 'Top Management', label: 'Top Management' },
              { value: 'Middle Management', label: 'Middle Management' },
              { value: 'Department Head', label: 'Department Head' },
              { value: 'Section Head', label: 'Section Head' },
            ]}
          />
          <LookupSelect label="Employee" value={posForm.employeeId} onChange={(v) => setPosForm({ ...posForm, employeeId: v })} fetchOptions={fetchEmployeeOptions} required />
          {posForm.positionLevel === 'Department Head' && (
            <LookupSelect label="Department" value={posForm.departmentId} onChange={(v) => setPosForm({ ...posForm, departmentId: v })} fetchOptions={fetchDepartmentOptions} required />
          )}
          <Input label="Order" type="number" value={posForm.positionOrder} onChange={(e) => setPosForm({ ...posForm, positionOrder: e.target.value })} />
          <Button type="submit" className="w-full">
            Add
          </Button>
        </form>
      </Modal>
    </div>
  );
}
